import { useEffect } from 'react';

import queryClient from '@src/app/providers/queryClient';
import { useAuthentication } from '@src/features/auth';
import {
  publishAlertRealtimeEvent,
  type AlertSummaryChangedEvent,
} from '@src/features/alerts/model';
import settings from '@src/shared/config';
import useAPIClient from '@src/shared/hooks/api/useAPIClient';

type WebSocketTicketResponse = {
  ticket: string;
  expires_in: number;
};

const HEARTBEAT_INTERVAL_MS = 25_000;
const MAX_RECONNECT_DELAY_MS = 30_000;
const INVALIDATION_DEBOUNCE_MS = 100;

function getAlertWebSocketUrl(ticket: string) {
  const url = new URL(import.meta.env.VITE_API_DOMAIN);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  url.pathname = '/ws/v1/alerts/';
  url.search = '';
  url.searchParams.set('ticket', ticket);
  return url.toString();
}

function parseAlertSummaryEvent(
  value: string,
): AlertSummaryChangedEvent | null {
  try {
    const event: unknown = JSON.parse(value);
    if (
      typeof event === 'object' &&
      event !== null &&
      'type' in event &&
      event.type === 'alerts.summary.changed' &&
      'reason' in event &&
      typeof event.reason === 'string'
    ) {
      const alertId =
        'alert_id' in event && typeof event.alert_id === 'number'
          ? event.alert_id
          : undefined;
      return {
        type: event.type,
        reason: event.reason,
        ...(alertId === undefined ? {} : { alertId }),
      };
    }
  } catch {
    return null;
  }
  return null;
}

function isPongMessage(value: string) {
  try {
    const event: unknown = JSON.parse(value);
    return (
      typeof event === 'object' &&
      event !== null &&
      'type' in event &&
      event.type === 'pong'
    );
  } catch {
    return false;
  }
}

export default function AlertSummarySocketController() {
  const { user } = useAuthentication();
  const userId = user?.id;
  const { request } = useAPIClient();

  useEffect(() => {
    if (userId === undefined) {
      return;
    }

    let stopped = false;
    let reconnectAttempt = 0;
    let socket: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let invalidationTimer: ReturnType<typeof setTimeout> | null = null;
    let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
    let receivedPong = true;

    const invalidateSummary = () =>
      queryClient.invalidateQueries({
        queryKey: ['root', 'alerts', 'summary'],
      });

    const scheduleSummaryInvalidation = () => {
      if (invalidationTimer !== null) {
        clearTimeout(invalidationTimer);
      }
      invalidationTimer = setTimeout(() => {
        invalidationTimer = null;
        void invalidateSummary();
      }, INVALIDATION_DEBOUNCE_MS);
    };

    const clearHeartbeat = () => {
      if (heartbeatTimer !== null) {
        clearInterval(heartbeatTimer);
        heartbeatTimer = null;
      }
    };

    const scheduleReconnect = () => {
      if (stopped || reconnectTimer !== null) {
        return;
      }
      const baseDelay = Math.min(
        1_000 * 2 ** reconnectAttempt,
        MAX_RECONNECT_DELAY_MS,
      );
      const jitter = Math.floor(Math.random() * 500);
      reconnectAttempt += 1;
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        void connect();
      }, baseDelay + jitter);
    };

    const connect = async () => {
      if (
        stopped ||
        socket?.readyState === WebSocket.OPEN ||
        socket?.readyState === WebSocket.CONNECTING
      ) {
        return;
      }

      try {
        const ticketResponse = (await request({
          url: 'auth/websocket-ticket/',
          method: 'POST',
        })) as WebSocketTicketResponse;

        if (stopped) {
          return;
        }

        const nextSocket = new WebSocket(
          getAlertWebSocketUrl(ticketResponse.ticket),
        );
        socket = nextSocket;
        nextSocket.onopen = () => {
          reconnectAttempt = 0;
          receivedPong = true;
          scheduleSummaryInvalidation();
          clearHeartbeat();
          heartbeatTimer = setInterval(() => {
            if (!socket || socket.readyState !== WebSocket.OPEN) {
              return;
            }
            if (!receivedPong) {
              socket.close();
              return;
            }
            receivedPong = false;
            socket.send(JSON.stringify({ type: 'ping' }));
          }, HEARTBEAT_INTERVAL_MS);
        };
        nextSocket.onmessage = (message) => {
          if (typeof message.data === 'string' && isPongMessage(message.data)) {
            receivedPong = true;
            return;
          }

          if (typeof message.data !== 'string') {
            return;
          }
          const event = parseAlertSummaryEvent(message.data);
          if (!event) {
            return;
          }

          if (settings.DEBUG) {
            console.info('Alerts summary changed', {
              reason: event.reason,
              alertId: event.alertId,
            });
          }
          publishAlertRealtimeEvent(event);
          scheduleSummaryInvalidation();
        };
        nextSocket.onerror = () => nextSocket.close();
        nextSocket.onclose = () => {
          if (socket === nextSocket) {
            socket = null;
          }
          clearHeartbeat();
          scheduleReconnect();
        };
      } catch {
        scheduleReconnect();
      }
    };

    const reconnectWhenOnline = () => {
      if (!socket || socket.readyState === WebSocket.CLOSED) {
        void connect();
      }
    };
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') {
        scheduleSummaryInvalidation();
        reconnectWhenOnline();
      }
    };

    window.addEventListener('online', reconnectWhenOnline);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    void connect();

    return () => {
      stopped = true;
      window.removeEventListener('online', reconnectWhenOnline);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer);
      }
      if (invalidationTimer !== null) {
        clearTimeout(invalidationTimer);
      }
      clearHeartbeat();
      socket?.close();
    };
  }, [request, userId]);

  return null;
}
